import { create } from 'zustand';
import { servicesContainer } from '@/core/di/services-container';
import {
  SheetMappingItem,
  ColumnMappingItem,
  SheetMappingRequest,
  ColumnMappingRequest,
  ImportConfigurationStore,
} from '../types/import-configuration-types';

/**
 * Import Configuration Store
 * Manages state for Excel import configuration (sheet mappings and column mappings)
 */
export const useImportConfigurationStore = create<ImportConfigurationStore>((set, get) => ({
  // Initial state
  sheetMappings: [],
  selectedSheetMapping: null,
  expandedMappingId: null,

  isLoading: false,
  isLoadingDetail: false,
  isSubmitting: false,
  error: null,
  submitError: null,

  sheetFormDialogOpen: false,
  editingSheetMapping: null,
  columnFormDialogOpen: false,
  editingColumnMapping: null,
  columnFormSheetId: null,
  deleteDialogOpen: false,
  itemToDelete: null,

  // ============================================================================
  // Sheet Mapping Actions
  // ============================================================================

  fetchSheetMappings: async () => {
    set({ isLoading: true, error: null });
    try {
      const services = servicesContainer.getServices();
      const response = await services.getSheetMappingsUseCase.execute();

      if (!response.isSuccess) {
        throw new Error(response.errorMessage || 'Failed to fetch sheet mappings');
      }

      set({
        sheetMappings: response.mappings || [],
        isLoading: false,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to fetch sheet mappings';
      set({ error: message, isLoading: false });
    }
  },

  fetchSheetMappingDetail: async (mappingId: number) => {
    set({ isLoadingDetail: true });
    try {
      const services = servicesContainer.getServices();
      const response = await services.getSheetMappingDetailUseCase.execute(mappingId);

      if (!response.isSuccess) {
        throw new Error(response.errorMessage || 'Failed to fetch sheet mapping details');
      }

      // Update the sheet mapping in the list with its column mappings
      const { sheetMappings } = get();
      const updatedMappings = sheetMappings.map(m =>
        m.mappingId === mappingId ? response.mapping : m
      );

      set({
        sheetMappings: updatedMappings,
        selectedSheetMapping: response.mapping,
        isLoadingDetail: false,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to fetch sheet mapping details';
      set({ error: message, isLoadingDetail: false });
    }
  },

  saveSheetMapping: async (data: SheetMappingRequest) => {
    set({ isSubmitting: true, submitError: null });
    try {
      const services = servicesContainer.getServices();
      const response = await services.saveSheetMappingUseCase.execute(data);

      if (!response.isSuccess || !response.mapping) {
        throw new Error(response.errorMessage || 'Failed to save sheet mapping');
      }

      // Refresh the list
      await get().fetchSheetMappings();

      set({
        isSubmitting: false,
        sheetFormDialogOpen: false,
        editingSheetMapping: null,
      });

      return response.mapping;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to save sheet mapping';
      set({ submitError: message, isSubmitting: false });
      throw error;
    }
  },

  deleteSheetMapping: async (mappingId: number) => {
    set({ isSubmitting: true, submitError: null });
    try {
      const services = servicesContainer.getServices();
      const response = await services.deleteSheetMappingUseCase.execute(mappingId);

      if (!response.isSuccess) {
        throw new Error(response.errorMessage || 'Failed to delete sheet mapping');
      }

      // Remove from local state
      const { sheetMappings, expandedMappingId, selectedSheetMapping } = get();
      set({
        sheetMappings: sheetMappings.filter(m => m.mappingId !== mappingId),
        expandedMappingId: expandedMappingId === mappingId ? null : expandedMappingId,
        selectedSheetMapping: selectedSheetMapping?.mappingId === mappingId ? null : selectedSheetMapping,
        isSubmitting: false,
        deleteDialogOpen: false,
        itemToDelete: null,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to delete sheet mapping';
      set({ submitError: message, isSubmitting: false });
      throw error;
    }
  },

  // ============================================================================
  // Column Mapping Actions
  // ============================================================================

  saveColumnMapping: async (data: ColumnMappingRequest) => {
    set({ isSubmitting: true, submitError: null });
    try {
      const services = servicesContainer.getServices();
      const response = await services.saveColumnMappingUseCase.execute(data);

      if (!response.isSuccess || !response.column) {
        throw new Error(response.errorMessage || 'Failed to save column mapping');
      }

      // Refresh the sheet mapping detail to get updated column mappings
      await get().fetchSheetMappingDetail(data.sheetMappingId);

      set({
        isSubmitting: false,
        columnFormDialogOpen: false,
        editingColumnMapping: null,
        columnFormSheetId: null,
      });

      return response.column;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to save column mapping';
      set({ submitError: message, isSubmitting: false });
      throw error;
    }
  },

  deleteColumnMapping: async (columnId: number) => {
    set({ isSubmitting: true, submitError: null });
    try {
      const services = servicesContainer.getServices();
      const response = await services.deleteColumnMappingUseCase.execute(columnId);

      if (!response.isSuccess) {
        throw new Error(response.errorMessage || 'Failed to delete column mapping');
      }

      // Update local state by removing the column from the sheet mapping
      const { sheetMappings, selectedSheetMapping } = get();
      const updatedMappings = sheetMappings.map(m => ({
        ...m,
        columnMappings: m.columnMappings?.filter(c => c.columnMappingId !== columnId) || [],
      }));

      set({
        sheetMappings: updatedMappings,
        selectedSheetMapping: selectedSheetMapping
          ? {
              ...selectedSheetMapping,
              columnMappings: selectedSheetMapping.columnMappings?.filter(
                c => c.columnMappingId !== columnId
              ) || [],
            }
          : null,
        isSubmitting: false,
        deleteDialogOpen: false,
        itemToDelete: null,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to delete column mapping';
      set({ submitError: message, isSubmitting: false });
      throw error;
    }
  },

  // ============================================================================
  // UI Actions
  // ============================================================================

  openSheetFormDialog: (mapping?: SheetMappingItem) => {
    set({
      sheetFormDialogOpen: true,
      editingSheetMapping: mapping || null,
      submitError: null,
    });
  },

  closeSheetFormDialog: () => {
    set({
      sheetFormDialogOpen: false,
      editingSheetMapping: null,
      submitError: null,
    });
  },

  openColumnFormDialog: (sheetMappingId: number, column?: ColumnMappingItem) => {
    set({
      columnFormDialogOpen: true,
      columnFormSheetId: sheetMappingId,
      editingColumnMapping: column || null,
      submitError: null,
    });
  },

  closeColumnFormDialog: () => {
    set({
      columnFormDialogOpen: false,
      columnFormSheetId: null,
      editingColumnMapping: null,
      submitError: null,
    });
  },

  openDeleteDialog: (type: 'sheet' | 'column', id: number, name: string) => {
    set({
      deleteDialogOpen: true,
      itemToDelete: { type, id, name },
      submitError: null,
    });
  },

  closeDeleteDialog: () => {
    set({
      deleteDialogOpen: false,
      itemToDelete: null,
      submitError: null,
    });
  },

  setExpandedMappingId: (mappingId: number | null) => {
    const { expandedMappingId, fetchSheetMappingDetail } = get();

    // If expanding a different row, fetch its details
    if (mappingId !== null && mappingId !== expandedMappingId) {
      fetchSheetMappingDetail(mappingId);
    }

    set({ expandedMappingId: mappingId });
  },

  clearSubmitError: () => {
    set({ submitError: null });
  },

  reset: () => {
    set({
      sheetMappings: [],
      selectedSheetMapping: null,
      expandedMappingId: null,
      isLoading: false,
      isLoadingDetail: false,
      isSubmitting: false,
      error: null,
      submitError: null,
      sheetFormDialogOpen: false,
      editingSheetMapping: null,
      columnFormDialogOpen: false,
      editingColumnMapping: null,
      columnFormSheetId: null,
      deleteDialogOpen: false,
      itemToDelete: null,
    });
  },
}));
